import { useMemo } from "react";
import { Option, Stack } from "@altimateai/lego";
import { FilterType, TableHeaderType } from "@/components/tables/types";
import { GetOpportunitiesDataResponse, OwnerListItem } from "../../../types";
import { getEffortLabel, getOpportunityStatus } from "../../../utils";
import OwnerAssignerDropdown from "../../../components/ownerAssignerDropdown/OwnerAssignerDropdown";
import EffortTag from "../../../components/effortTag/EffortTag";
import StatusTag from "../../../components/statusTag/StatusTag";
import {
  DetailsCell,
  ResourceCell,
  MoneySavingsCell,
  TimeSavingsCell,
} from "../../../components/opportunities/cells";
import { EFFORT_OPTIONS, STATUS_OPTIONS } from "../../../constants";

interface Datastore {
  name: string;
  instance_id?: string | number | null;
  [key: string]: unknown;
}

interface UseOpportunitiesTableHeadersProps {
  activeUserList: Option[];
  ownerList: OwnerListItem[] | null;
  onOwnerChange: (owner: string, opportunity_id: string) => void;
  startDate: Date;
  endDate: Date;
  opportunityNameOptions: Option[];
  instanceOptions: Option[];
  datastores: Datastore[];
  // When true, the Status column header omits its filter dropdown.
  // The Implemented Opportunities tab reads from a table that only
  // holds IMPLEMENTED rows, so a status filter chip can't change the
  // result set — surfacing the dropdown there would just confuse users.
  hideStatusFilter?: boolean;
}

export function useOpportunitiesTableHeaders({
  activeUserList,
  ownerList,
  onOwnerChange,
  startDate,
  endDate,
  opportunityNameOptions,
  instanceOptions,
  datastores,
  hideStatusFilter = false,
}: UseOpportunitiesTableHeadersProps): TableHeaderType<GetOpportunitiesDataResponse>[] {
  return useMemo(() => {
    return [
      {
        id: 0,
        label: "Details",
        uid: "details",
        width: 350,
        render: (item: GetOpportunitiesDataResponse) => (
          <DetailsCell
            resource_name={item.resource_name}
            resource_type={item.resource_type}
            resource_uri={item.resources?.[0]?.resource_uri || ""}
            opportunity_name={item.opportunity_name}
            description={item.description || ""}
            last_seen={item.last_seen}
            first_seen={item.first_seen}
            count={item.count}
            total_count={item.total_count || 0}
          />
        ),
      },
      {
        id: 1,
        label: "Resources",
        uid: "resources",
        width: 220,
        render: (item: GetOpportunitiesDataResponse) => <ResourceCell resources={item.resources} />,
      },
      {
        id: 2,
        label: "Money Savings",
        uid: "money_savings",
        width: 140,
        render: (item: GetOpportunitiesDataResponse) => (
          <MoneySavingsCell
            savings={{
              min: parseFloat(item.money_savings.min),
              max: parseFloat(item.money_savings.max),
            }}
          />
        ),
        tooltipInfo:
          "Possible money savings during the selected time period",
        sort: {
          sortSearchKey: "sort",
          sortOrderSearchKey: "sort_order",
          sortParamValue: "cost_savings_max",
        },
      },
      {
        id: 3,
        label: "Time Savings",
        uid: "time_savings",
        width: 140,
        render: (item: GetOpportunitiesDataResponse) => (
          <TimeSavingsCell time={item.time_savings} />
        ),
        tooltipInfo:
          "Possible time savings during the selected time period",
        sort: {
          sortSearchKey: "sort",
          sortOrderSearchKey: "sort_order",
          sortParamValue: "time_savings_max",
        },
      },
      {
        id: 4,
        label: "Effort",
        uid: "effort",
        width: 100,
        filter: {
          filterType: FilterType.DROPDOWN,
          searchKey: "effort",
          options: EFFORT_OPTIONS,
        },
        render: (item: GetOpportunitiesDataResponse) => (
          <EffortTag effort={getEffortLabel(item.effort)} />
        ),
      },
      {
        id: 5,
        label: "Owner",
        uid: "owner",
        width: 140,
        filter: {
          filterType: FilterType.DROPDOWN,
          searchKey: "owner",
          options: activeUserList,
        },
        render: (item: GetOpportunitiesDataResponse) => (
          <OwnerAssignerDropdown
            assignedOwner={ownerList
              ?.find(o => o.opportunity_id === item.opportunity_id)
              ?.owner?.toString()}
            setAssignedOwner={(owner: string) => {
              onOwnerChange(owner, item.opportunity_id);
            }}
            opportunity_id={item.opportunity_id}
            startDate={startDate?.toISOString()}
            endDate={endDate?.toISOString()}
            userOptions={activeUserList}
          />
        ),
      },
      {
        id: 6,
        label: "Status",
        uid: "status",
        width: 120,
        tooltipInfo: (
          <Stack direction="column" gap="md">
            <div>
              Active: This opportunity has been discovered but not implemented or dismissed yet.
            </div>
            <div>Implemented: This opportunity has been implemented already</div>
            <div>Dismissed: This opportunity cannot be implemented and it has been dismissed.</div>
          </Stack>
        ),
        ...(hideStatusFilter
          ? {}
          : {
              filter: {
                filterType: FilterType.DROPDOWN,
                searchKey: "status",
                options: STATUS_OPTIONS,
              },
            }),
        render: (item: GetOpportunitiesDataResponse) => (
          <StatusTag status={getOpportunityStatus(item.status)} />
        ),
      },
      {
        id: 7,
        label: "Opportunity",
        uid: "opportunity_name",
        width: 180,
        filter: {
          filterType: FilterType.DROPDOWN,
          searchKey: "opportunity_name",
          options: opportunityNameOptions,
        },
        render: (item: GetOpportunitiesDataResponse) => (
          <div className="al-break-words">{item.opportunity_name}</div>
        ),
      },
      {
        id: 8,
        label: "Instance",
        uid: "instance",
        width: 200,
        filter: {
          filterType: FilterType.DROPDOWN,
          searchKey: "instance",
          options: instanceOptions,
        },
        render: (item: GetOpportunitiesDataResponse) => (
          <div>{datastores?.find(d => d.instance_id === item.instance_id)?.name}</div>
        ),
      },
    ];
  }, [
    activeUserList,
    ownerList,
    onOwnerChange,
    startDate,
    endDate,
    opportunityNameOptions,
    instanceOptions,
    datastores,
    hideStatusFilter,
  ]);
}
